import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      setLoading(true);
      const user = await login(email, password);
      toast.success(`Bem-vindo, ${user.name}!`);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email ou senha incorretos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="p-4">
        <Link to="/" className="flex items-center gap-2" data-testid="login-logo">
          <div className="w-10 h-10 rounded-xl gradient-hero flex items-center justify-center">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-lg text-primary leading-tight">KITS TOP</h1>
            <p className="text-xs text-muted-foreground -mt-1">de ALIMENTOS</p>
          </div>
        </Link>
      </header>

      {/* Login Form */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <h2 className="font-heading text-2xl font-bold text-foreground text-center mb-2">
              Entrar
            </h2>
            <p className="text-muted-foreground text-center mb-8">
              Acesse sua conta para continuar
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="rounded-xl"
                  required
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="rounded-xl pr-10"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full rounded-xl shadow-primary"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>

              <div className="text-center">
                <Link
                  to="/esqueci-senha"
                  className="text-sm text-primary font-medium hover:underline"
                  data-testid="forgot-password-link"
                >
                  Esqueci minha senha
                </Link>
              </div>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              Não tem uma conta?{' '}
              <Link to="/cadastro" className="text-primary font-medium hover:underline" data-testid="register-link">
                Cadastre-se
              </Link>
            </p>
          </div>

          {/* Admin Access */}
          <p className="text-center text-sm text-muted-foreground mt-4">
            É administrador?{' '}
            <Link to="/admin/login" className="text-primary font-medium hover:underline" data-testid="admin-login-link">
              Acesse o painel
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
